import {
  Accordion,
  Checkbox,
  Group,
  RangeSlider,
  Rating,
  Space,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconCategory,
  IconCreditCard,
  IconLanguage,
  IconRating12Plus,
  IconStar,
  IconStarFilled,
  IconStars,
} from '@tabler/icons-react';
import { primaryColor } from '../../../../../constants/theme';
import { useEffect, useMemo } from 'react';
import { CATEGORIES, LANGUAGES } from '../../../../../constants/options';
import { useSearch } from '../SearchContext';
import { FilterKeys } from './filters';
import { useQuery } from '@tanstack/react-query';
import { getSearchResults } from '../../../../../services/SearchService';
import { useApp } from '../../../../../context/AppContext';
import { useSearchParams } from 'react-router-dom';

export const FilterTitle = ({
  title,
  filterCount,
}: {
  title: string;
  filterCount: number;
}) => {
  return (
    <Group gap={3}>
      <Text>{title}</Text>
      <Text c={primaryColor} fz="sm">
        ({filterCount})
      </Text>
    </Group>
  );
};

export const RateFilter = () => {
  const { token } = useApp();
  const { ratingFilters, setRatingFilters, getFilterQuery, searchExpression } =
    useSearch();
  const { data: sessionFeeFacetData, isLoading } = useQuery({
    queryKey: [
      'facets',
      getFilterQuery(FilterKeys.FEEDBACK),
      searchExpression?.searchText,
    ],
    queryFn: () =>
      getSearchResults(
        {
          ...searchExpression,
          size: 0,
          filterExpression: getFilterQuery(FilterKeys.FEEDBACK),
        },
        token
      ),
    enabled: !!token,
    retry: true,
  });

  useEffect(() => {}, []);

  const feedbackFacets = useMemo(() => {
    return sessionFeeFacetData?.facets[FilterKeys.FEEDBACK] || [];
  }, [sessionFeeFacetData]);

  const { countZero, countOne, countTwo, countThree, countFour, countFive } =
    useMemo(() => {
      let countZero = 0;
      let countOne = 0;
      let countTwo = 0;
      let countThree = 0;
      let countFour = 0;
      let countFive = 0;

      feedbackFacets?.forEach((facet) => {
        switch (facet.value) {
          case 0:
            countZero = facet.count;
            break;
          case 1:
            countOne = facet.count;
            break;
          case 2:
            countTwo = facet.count;
            break;
          case 3:
            countThree = facet.count;
            break;
          case 4:
            countFour = facet.count;
            break;
          case 5:
            countFive = facet.count;
            break;
        }
      });

      return {
        countZero,
        countOne,
        countTwo,
        countThree,
        countFour,
        countFive,
      };
    }, [feedbackFacets]);

  const handleSelect = (value: number) => {
    if (ratingFilters.has(value)) {
      const newSet = new Set(ratingFilters);
      newSet.delete(value);
      setRatingFilters(newSet);
    } else {
      const newSet = new Set(ratingFilters);
      newSet.add(value);
      setRatingFilters(newSet);
    }
  };

  return (
    <Accordion.Item value={'rating'} key={'rating'} pb={'lg'}>
      <Accordion.Control icon={<IconStars color={primaryColor} />}>
        <FilterTitle title="Rating" filterCount={ratingFilters.size} />
      </Accordion.Control>
      <Accordion.Panel>
        <Stack>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={5} readOnly />}
              disabled={countFive === 0}
              onChange={() => handleSelect(5)}
            />
            <Text c="dimmed" fz="sm">
              ({countFive})
            </Text>
          </Group>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={4} readOnly />}
              disabled={countFour === 0 || isLoading}
              onChange={() => handleSelect(4)}
              checked={ratingFilters.has(4)}
            />
            <Text c="dimmed" fz="sm">
              ({countFour})
            </Text>
          </Group>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={3} readOnly />}
              disabled={countThree === 0 || isLoading}
              onChange={() => handleSelect(3)}
              checked={ratingFilters.has(3)}
            />
            <Text c="dimmed" fz="sm">
              ({countThree})
            </Text>
          </Group>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={2} readOnly />}
              disabled={countTwo === 0 || isLoading}
              onChange={() => handleSelect(2)}
              checked={ratingFilters.has(2)}
            />
            <Text c="dimmed" fz="sm">
              ({countTwo})
            </Text>
          </Group>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={1} readOnly />}
              disabled={countOne === 0 || isLoading}
              onChange={() => handleSelect(1)}
              checked={ratingFilters.has(1)}
            />
            <Text c="dimmed" fz="sm">
              ({countOne})
            </Text>
          </Group>
          <Group>
            <Checkbox
              color={primaryColor}
              label={<Rating value={0} readOnly />}
              disabled={countZero === 0 || isLoading}
              onChange={() => handleSelect(0)}
              checked={ratingFilters.has(0)}
            />
            <Text c="dimmed" fz="sm">
              ({countZero})
            </Text>
          </Group>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export const Fee = () => {
  const { token } = useApp();
  const { feeFilters, searchExpression, getFilterQuery, setFeeFilters } =
    useSearch();
  const { data: sessionFeeFacetData, isLoading } = useQuery({
    queryKey: ['facets', getFilterQuery(FilterKeys.RATE)],
    queryFn: () =>
      getSearchResults(
        {
          ...searchExpression,
          size: 0,
          filterExpression: getFilterQuery(FilterKeys.RATE),
        },
        token
      ),
    enabled: !!token,
  });

  const [min, max] = [0, 500];

  const handleSliderChange = (value: [number, number]) => {
    setFeeFilters({ min: value[0], max: value[1] });
  };

  return (
    <Accordion.Item value={'fee'} key={'fee'} pb={'lg'}>
      <Accordion.Control icon={<IconCreditCard color={primaryColor} />}>
        <FilterTitle title="Session Fee" filterCount={feeFilters ? 1 : 0} />
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap={2}>
          <Space h={20} />
          <RangeSlider
            color={primaryColor}
            min={min}
            max={max}
            step={5}
            value={[feeFilters?.min || 0, feeFilters?.max || max]}
            onChange={handleSliderChange}
            disabled={isLoading}
          />
          <Group justify="space-between">
            <Text c="dimmed">Min: {feeFilters?.min || 0}</Text>
            <Text c="dimmed">Max: {feeFilters?.max || max}</Text>
          </Group>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export const Languages = () => {
  const { token } = useApp();
  const {
    languageFilters,
    setLanguageFilters,
    getFilterQuery,
    searchExpression,
  } = useSearch();

  const { data: languageFacetsData, isLoading } = useQuery({
    queryKey: [
      'facets',
      getFilterQuery(FilterKeys.LANGUAGES),
      searchExpression?.searchText,
    ],
    queryFn: () =>
      getSearchResults(
        {
          ...searchExpression,
          size: 0,
          filterExpression: getFilterQuery(FilterKeys.LANGUAGES),
        },
        token
      ),
    enabled: !!token,
  });

  const languageCountMap = useMemo(() => {
    const map = new Map<string, number>();
    (languageFacetsData?.facets[FilterKeys.LANGUAGES] ?? [])?.forEach(
      (facet) => {
        map.set(facet.value as string, facet.count);
      }
    );
    return map;
  }, [languageFacetsData]);

  const handleSelect = (value: string) => {
    if (languageFilters.has(value)) {
      setLanguageFilters((prev) => {
        const newSet = new Set(prev);
        newSet.delete(value);
        return newSet;
      });
    } else {
      setLanguageFilters((prev) => new Set(prev).add(value));
    }
  };

  return (
    <Accordion.Item value={'languages'} key={'languages'} pb={'lg'}>
      <Accordion.Control icon={<IconLanguage color={primaryColor} />}>
        <FilterTitle title="Languages" filterCount={languageFilters.size} />
      </Accordion.Control>
      <Accordion.Panel>
        <Stack>
          {LANGUAGES.map((lang) => (
            <Checkbox
              onChange={() => handleSelect(lang.name)}
              color={primaryColor}
              key={lang.id}
              label={
                lang.name + ' (' + (languageCountMap.get(lang.name) || 0) + ')'
              }
              disabled={
                languageCountMap.get(lang.name) === undefined || isLoading
              }
              checked={languageFilters.has(lang.name)}
            />
          ))}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export const Categories = () => {
  const { token } = useApp();
  const {
    categoryFilters,
    setCategoryFilters,
    getFilterQuery,
    searchExpression,
  } = useSearch();
  const { data: categoryFacetData, isLoading } = useQuery({
    queryKey: [
      'facets',
      getFilterQuery(FilterKeys.CATEGORIES),
      searchExpression?.searchText,
    ],
    queryFn: () =>
      getSearchResults(
        {
          ...searchExpression,
          size: 0,
          filterExpression: getFilterQuery(FilterKeys.CATEGORIES),
        },
        token
      ),
    enabled: !!token,
  });

  const categoryCountMap = useMemo(() => {
    const map = new Map<string, number>();
    (categoryFacetData?.facets[FilterKeys.CATEGORIES] || [])?.forEach(
      (facet) => {
        map.set(facet.value as string, facet.count);
      }
    );
    return map;
  }, [categoryFacetData]);

  const handleSelect = (value: string) => {
    if (categoryFilters.has(value)) {
      setCategoryFilters((prev) => {
        const newSet = new Set(prev);
        newSet.delete(value);
        return newSet;
      });
    } else {
      setCategoryFilters((prev) => new Set(prev).add(value));
    }
  };

  return (
    <Accordion.Item value={'categories'} key={'categories'} pb={'lg'}>
      <Accordion.Control icon={<IconCategory color={primaryColor} />}>
        <FilterTitle title="Categories" filterCount={categoryFilters.size} />
      </Accordion.Control>
      <Accordion.Panel>
        <Stack>
          {CATEGORIES.map((cat) => (
            <Checkbox
              onChange={() => handleSelect(cat.name)}
              color={primaryColor}
              key={cat.id}
              label={
                cat.name + ' (' + (categoryCountMap.get(cat.name) || 0) + ')'
              }
              disabled={
                categoryCountMap.get(cat.name) === undefined || isLoading
              }
              checked={categoryFilters.has(cat.name)}
            />
          ))}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};
